import { getErrorDetail } from "components/utility/utils";
import { fetchOrchidAPI } from "utils/api";
import { reportError, reportProblem } from "utils/frontend_error_reporting";
import { orchid_decode_key } from "utils/OrchidEncryption";
import { DriveEntryType, OrchidDriveEntryKeys } from "../types";

export async function createEntry(
  storageDrive: StorageDrive,
  entryType: DriveEntryType,
): Promise<OrchidDriveEntryKeys> {
  const response = await fetchOrchidAPI(`/api/drive/keys/create_entry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      storage_drive: storageDrive,
      entry_type: entryType,
    }),
  });
  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(getErrorDetail(responseText));
  }

  const result = await response.json();

  return {
    entryId: result.entry_id,
    dataCipherKey: await orchid_decode_key(result.data_cipher_key),
    metaDataCipherKey: await orchid_decode_key(result.meta_data_cipher_key),
  };
}

export async function requestEncryptionKeys(
  entryIds: string[],
): Promise<{ [name: string]: OrchidDriveEntryKeys }> {
  const response = await fetchOrchidAPI(`/api/drive/keys/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry_ids: entryIds }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    const errorDetails = getErrorDetail(responseText);
    await reportProblem({
      reporter_module: "orchidDrive",
      context: { func: "requestEncryptionKeys", error: "request failed" },
      frontend_error: `failed to request keys: ${errorDetails}`,
    });
    throw new Error(errorDetails);
  }

  let responseData: any = null;
  try {
    responseData = await response.json();
  } catch (err: any) {
    await reportError({
      reporter_module: "orchidDrive",
      context: {
        func: "requestEncryptionKeys",
        error: "failed to decode message",
      },
      err,
    });
    throw new Error("failed to decode encryption keys response");
  }

  const entryKeys = {} as { [name: string]: OrchidDriveEntryKeys };
  for (const entryKey of responseData.key_entries) {
    try {
      entryKeys[entryKey.entry_id] = {
        entryId: entryKey.entry_id,
        dataCipherKey: await orchid_decode_key(entryKey.data_cipher_key),
        metaDataCipherKey: await orchid_decode_key(
          entryKey.meta_data_cipher_key,
        ),
      };
    } catch (err: any) {
      await reportError({
        reporter_module: "orchidDrive",
        context: {
          func: "requestEncryptionKeys",
          error: "failed to decode keys",
        },
        err,
      });
      throw new Error("failed to decode encryption key");
    }
  }

  return entryKeys;
}
